import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { getScopedPolicyWithParents, patchPolicy } from '@/lib/services/policy.service';
import useToast from '@/hooks/use-toast.hook';
import { PolicyModel } from '@/lib/models/policy-model/policy.model';
import FormSection from '@/components/form/form-section';
import useQueryHelper from '@/hooks/use-query-helper';
import InputControl from '@/components/form/input-control';
import useFormHandle from '@/hooks/use-form-handle.hook';
import FormButtons from '@/components/form/form-buttons';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import useFeature from '@/hooks/use-feature.hook';
import PortalDeprecatedToast from '@/components/portal-deprecated-toast';
import PageFeatureToast from '@/components/page-feature-toast';
import { Box, Grid, Stack } from '@mui/material';
import InlineToast from '@/components/toasts/inline';
import { getAllPrivateModels } from '@/lib/services/private-model.service';
import ModelOption from '@/components/model-option';
import { cloneDeep } from 'lodash';
import { FieldError } from '@/lib/services';
import FormLabel from '@/components/form-label';
import DatasourceEntityList from '@/components/form/datasource-entity-list';
import {
  AssistantConfig,
  AssistantEntity,
  DatasourceConfig,
  DatasourceEntity,
  ModelEntity,
  PortalConfig,
} from '@/lib/models/policy-model/portal';
import AgentEntityList from '@/components/form/agent-entity-list';
import ModelEntityList from '@/components/form/model-entity-list';
import Chip from '@/components/chip';
import FormSectionSubtitle from '@/components/form/form-section/form-section-subtitle';

const fields = ['contextDataSources', 'assistants', 'portal'];
const formModel = new PolicyModel();

interface Props {
  policyId: string;
}

const PortalForm: FC<Props> = ({ policyId }) => {
  const { errorToast, toast } = useToast();
  const { canChangePolicy, canChangeAgents, hasPortalChat } = useFeature();
  const isPortalChatDisabled = !hasPortalChat;

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schemaSlice(fields),
    onSubmit: async (values, { setSubmitting, resetForm, setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      const updateValues = cloneDeep(values);

      const { patched, error, fieldErrors } = await patchPolicy(
        policyId,
        updateValues.propSlice(fields)
      );

      setSubmitting(false);

      if (patched) {
        resetForm({ values });
        toast('The policy was updated');
        return;
      }

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      errorToast(error || 'The policy was not updated');
    },
  });

  const query = useQuery([QueryKey.ScopedPolicyWithParents, policyId], async () =>
    getScopedPolicyWithParents(policyId)
  );
  const { data: policyData } = query;
  const [policy, defaultPolicy] = policyData || [];
  const { isDefault: isDefaultPolicy } = policy || { isDefault: false };

  const privateModelsQuery = useQuery([QueryKey.PrivateModelsList], async () =>
    getAllPrivateModels()
  );
  const { data: privateModels } = privateModelsQuery;
  const { showLoader: privateModelsLoading } = useQueryHelper(privateModelsQuery);

  const { values, setFieldValue } = formHandle;

  const handleChangeDatasourceAccess = (access: DatasourceEntity[] | undefined) => {
    const updated = values.contextDataSources
      ? cloneDeep(values.contextDataSources)
      : new DatasourceConfig();
    updated.access = access ?? [];
    setFieldValue('contextDataSources', updated);
  };

  const handleChangeAgentAccess = (access: AssistantEntity[] | undefined) => {
    const updated = values.assistants ? cloneDeep(values.assistants) : new AssistantConfig();
    updated.access = access ?? [];
    setFieldValue('assistants', updated);
  };

  const handleChangeModelAccess = (models: ModelEntity[] | undefined) => {
    const updated = values.portal ? cloneDeep(values.portal) : new PortalConfig();
    updated.availablePrivateModels = models ?? [];
    setFieldValue('portal', updated);
  };

  const defaultPrivateModelOptions = useMemo(() => {
    return (privateModels || []).map(({ name, id, modelId }) => {
      const isDefaultPolicyDefault = id === defaultPolicy?.portal?.defaultModelId;

      const compName =
        !isDefaultPolicy && isDefaultPolicyDefault ? (
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>{name}</Box>
            <Chip label="Default Policy" size="x-small" />
          </Stack>
        ) : (
          name
        );

      return {
        label: <ModelOption name={compName} modelId={modelId} />,
        value: id,
        disabled: !values.portal?.availablePrivateModelIds?.includes(id),
      };
    });
  }, [privateModels, values.portal?.availablePrivateModelIds, defaultPolicy, isDefaultPolicy]);

  if (privateModelsLoading) {
    return;
  }

  const hasPrivateModel = !!privateModels?.length;

  return (
    <Form formHandle={formHandle} query={query}>
      <PortalDeprecatedToast />
      <PageFeatureToast featureId="change-policy" can={canChangePolicy} />

      <FormSection
        title="Allow Access to Portal Chat"
        scrollId="portal-access"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>Enable or disable access to Portal Chat.</FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InputControl
              label=""
              name="portal.enabled"
              type="on-off-toggle-button"
              formHandle={formHandle}
              disabled={isPortalChatDisabled}
              readonly={!canChangePolicy}
              inline={false}
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Portal Private Model Settings"
        scrollId="portal-private-model-settings"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>
          Choose which private models are available, and the default, when a user is redirected to
          Portal Chat.
        </FormSectionSubtitle>
        <Grid container columnSpacing={8} rowSpacing={4}>
          <Grid item xs={12}>
            <FormLabel>Default Private Model</FormLabel>
            <Box width="450px">
              <InputControl
                label=""
                name="portal.defaultModelId"
                type="select"
                disabled={!hasPrivateModel || isPortalChatDisabled}
                options={defaultPrivateModelOptions}
                formHandle={formHandle}
                readonly={!canChangePolicy}
                withNotSet={!isDefaultPolicy}
              />
            </Box>

            {!privateModelsLoading && !hasPrivateModel && (
              <Box mt={2.5}>
                <InlineToast
                  level="warning"
                  message="Enable a Private Model to enable this feature"
                />
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <FormLabel>Enabled Private Models</FormLabel>
            <ModelEntityList
              name="portal.availablePrivateModels"
              label="Enabled Private Models"
              readonly={!canChangePolicy}
              disabled={!hasPrivateModel || isPortalChatDisabled}
              value={values.portal?.availablePrivateModels}
              onChange={handleChangeModelAccess}
              defaultModelId={values.portal?.defaultModelId}
              emptyMessage="No private models available"
              withNotSet={!isDefaultPolicy}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Data Source Assignment"
        scrollId="data-source-assignment"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>Choose which data sources are available.</FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <DatasourceEntityList
              name="contextDataSources"
              disabled={isPortalChatDisabled}
              readonly={!canChangePolicy}
              value={values.contextDataSources?.access}
              onChange={handleChangeDatasourceAccess}
              policy={policy!}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormSection
        title="Assistant Assignment"
        scrollId="assistant-assignment"
        disabled={isPortalChatDisabled}
      >
        <FormSectionSubtitle>Choose which assistants are available.</FormSectionSubtitle>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <AgentEntityList
              name="assistants"
              label="Assistants"
              disabled={isPortalChatDisabled}
              readonly={!canChangeAgents}
              value={values.assistants?.access}
              onChange={handleChangeAgentAccess}
              policy={policy!}
            />
          </Grid>
        </Grid>
      </FormSection>

      <FormButtons formHandle={formHandle} readonly={!canChangePolicy || isPortalChatDisabled} />
    </Form>
  );
};

export default PortalForm;
